<script setup lang="ts">
  import { computed, type ComputedRef, watch, onMounted, ref } from 'vue'
  import SelectField, { type Option } from '@/components/admin/form/SelectField.vue'
  import * as yup from 'yup'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import type { CustomerCase, IntegrationError } from '@/types/generated/types.gen'
  import { RoleEnum } from '@/types/generated/types.gen'
  import { useTranslation } from '@/composables/useTranslation'
  import type { GenericObject } from 'vee-validate'
  import InputField from '@/components/admin/form/InputField.vue'
  import { useFormWithValidation } from '@/composables/useFormWithValidation'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { RelationshipTypeEnum, EntityTypeEnum } from '@/types/generated/types.gen'
  import { KleosService } from '@/types/generated/services.gen'
  import { ApiError } from '@/types/generated/core/ApiError'
  import { useRouter } from 'vue-router'
  import { useUserStore } from '@/store/userStore'
  import { ROUTES } from '@/router/routes'

  const props = defineProps<{
    debounceDelay?: number
    throttleInterval?: number
    isOpponentExported: boolean
    selectedEntitiesRelationships?: Array<string>
    mappedEntityTypes?: Array<string>
  }>()

  const emit = defineEmits(['update:payload'])
  const showAuthModal = ref(false)
  const isKleosExpiredToken = ref(false)
  const isKleosMissingToken = ref(false)
  const router = useRouter()

  const { t } = useTranslation({
    en: {
      kleosSection: 'Kleos Information',
      typeId: 'Type',
      typeIdRequired: 'A Type is required',
      creationDate: 'Creation Date',
      creationDateRequired: 'Creation Date required',
      name: 'Rubrum',
      nameRequired: 'Rubrum required',
      description: 'Description',
      authExpiredTitle: 'Kleos Authentication Expired',
      missingAuthTitle: 'Kleos Authentication Missing',
      authExpiredMessage: 'Your Kleos authentication has expired.',
      missingAuthMessage: 'An authentication to Kleos should first be done.',
      adminShouldAuthMessage: 'Please authenticate to continue using Kleos export functionality.',
      adminShouldReauthMessage:
        'Please re-authenticate to continue using Kleos export functionality.',
      userShouldContactAdminMessage:
        'Please contact your Law firm administrator to authenticate to Kleos.',
      reAuthenticate: 'Re-authenticate',
      cancel: 'Cancel'
    },
    de: {
      kleosSection: 'Kleos Information',
      typeId: 'Typ',
      typeIdRequired: 'Typ ist erforderlich',
      creationDate: 'Erstellungsdatum',
      creationDateRequired: 'Erstellungsdatum ist erforderlich',
      name: 'Rubrum',
      nameRequired: 'Rubrum ist erforderlich',
      description: 'Beschreibung',
      authExpiredTitle: 'Kleos-Authentifizierung abgelaufen',
      missingAuthTitle: 'Kleos-Authentifizierung fehlt',
      authExpiredMessage: 'Ihre Kleos-Authentifizierung ist abgelaufen.',
      missingAuthMessage: 'Eine Authentifizierung bei Kleos sollte zuerst vorgenommen werden.',
      adminShouldAuthMessage:
        'Bitte authentifizieren Sie sich, um die Kleos-Exportfunktionalität weiterhin nutzen zu können.',
      adminShouldReauthMessage:
        'Bitte authentifizieren Sie sich erneut, um die Kleos-Exportfunktionalität weiterhin nutzen zu können.',
      userShouldContactAdminMessage:
        'Bitte wenden Sie sich an Ihren Kanzleiadministrator, um sich bei Kleos zu authentifizieren.',
      reAuthenticate: 'Erneut authentifizieren',
      cancel: 'Abbrechen'
    }
  })

  const caseStore = useCaseStore()
  const caseRecord = computed<CustomerCase | null>(() => caseStore.caseRecord)

  // Fetch Kleos data to refresh the UI without page reload
  const fetchKleosData = async () => {
    try {
      // Fetch case types to refresh the data
      await KleosService.kleosCaseTypesRetrieve()

      // Refresh the case record if needed
      if (caseStore && caseStore.loadCase) {
        await caseStore.loadCase(router.currentRoute.value.params.uuid.toString())
      }

      return true
    } catch (error) {
      return false
    }
  }

  // Check Kleos token validity by fetching case types
  const checkKleosTokenValidity = async () => {
    try {
      await KleosService.kleosCaseTypesRetrieve()
      return true
    } catch (error) {
      if (error instanceof ApiError && error.body) {
        const errorBody: IntegrationError = error.body as IntegrationError
        const statusActions: { [key: string]: () => void } = {
          KLEOS_API_TOKEN_EXPIRED: () => {
            isKleosExpiredToken.value = true
          },
          KLEOS_API_TOKEN_MISSING: () => {
            isKleosMissingToken.value = true
          }
        }

        if (statusActions[errorBody.errorType]) {
          showAuthModal.value = true
          statusActions[errorBody.errorType]()
          return false
        }
      }
      return false
    }
  }

  const userStore = useUserStore()
  const { fetchUser } = userStore
  const userRole = computed(() => userStore.user?.role)

  const handleReAuthenticate = () => {
    const kleosAuthPath = ROUTES.APP.SETTINGS.LAW_OFFICE_SOFTWARE.KLEOS_OAUTH
    const width = 800
    const height = 700
    const left = (window.screen.width - width) / 2
    const top = (window.screen.height - height) / 2

    // Open the popup window with the Kleos authorization page
    const popup = window.open(
      kleosAuthPath,
      'KleosAuthentication',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`
    )

    if (popup) {
      // Set up polling regardless of popup status
      const checkTokenInterval = setInterval(async () => {
        try {
          // Poll the Kleos token status to check if authentication is complete
          const isValid = await checkKleosTokenValidity()

          if (isValid) {
            // Authentication successful
            popup.close()
            clearInterval(checkTokenInterval)
            fetchKleosData()
            closeAuthModal()
          }
        } catch (error) {
          // Check if we should stop polling (either due to too many attempts or actual popup closure)
          if (popup.closed) {
            clearInterval(checkTokenInterval)
            fetchKleosData() // Still refresh data in case auth completed but we missed it
          }
        }
      }, 2000)
    } else {
      // If popup was blocked, fall back to redirect
      window.location.href = kleosAuthPath
    }
  }

  // Close the auth modal
  const closeAuthModal = () => {
    showAuthModal.value = false
    isKleosExpiredToken.value = false
    isKleosMissingToken.value = false
  }

  const isClientEntity = (entity: any) => {
    if (!entity || !entity.relationships) return false
    return entity.relationships.some(
      (r: any) => r.relationship_type === RelationshipTypeEnum.ENTITIES_CLIENT
    )
  }

  const isOpponentEntity = (entity: any) => {
    if (!entity || !entity.relationships) return false
    return entity.relationships.some(
      (r: any) => r.relationship_type === RelationshipTypeEnum.ENTITIES_OPPONENT
    )
  }

  const getClientEntity = computed(() => {
    if (!caseRecord.value?.data.entities) return null
    return caseRecord.value.data.entities.find((entity) => isClientEntity(entity))
  })

  const getOpponentEntity = computed(() => {
    if (!caseRecord.value?.data.entities) return null
    return caseRecord.value.data.entities.find((entity) => isOpponentEntity(entity))
  })

  const transformKleosCaseTypeOptions = (data: any[] | undefined): Option[] => {
    if (!data) return []
    return data.map((item) => {
      return {
        value: item.id,
        title: item.name
      }
    })
  }

  const findMatchingKleosType = (lawField: string | undefined, options: Option[]): string => {
    if (!lawField || !options.length) return options[0]?.value || ''

    // Convert both strings to lowercase and remove special characters for comparison
    const normalizeName = (str: string) => str.toLowerCase().replace(/[^a-z0-9]/g, '')

    const normalizedLawField = normalizeName(lawField)

    // Try to find a matching type
    const matchingOption = options.find(
      (option) =>
        normalizeName(option.title).includes(normalizedLawField) ||
        normalizedLawField.includes(normalizeName(option.title))
    )

    return matchingOption?.value || options[0]?.value || ''
  }

  const kleosCaseTypeOptions: ComputedRef<Option[]> = computed(() => {
    return transformKleosCaseTypeOptions(caseRecord.value?.org?.kleos_case_type_options as any)
  })

  const initialTypeId = computed(() => {
    const options = kleosCaseTypeOptions.value
    const lawField = caseRecord.value?.data?.law?.field
    return findMatchingKleosType(lawField, options)
  })

  const initialName = computed(() => {
    if (!caseRecord.value) return ''

    const clientEntity = getClientEntity.value
    const opponentEntity = getOpponentEntity.value
    const mappedTypes = props.mappedEntityTypes || ['client'] // Default to client

    if (!clientEntity) return ''

    // Format client name based on entity type
    const clientName =
      clientEntity.entity?.entity_type === EntityTypeEnum.LEGAL_PERSON
        ? `${clientEntity.entity?.company_name || ''} ${clientEntity.entity?.company_legal_form || 'GmbH'}`
        : clientEntity.entity?.last_name || ''

    // Default title with just the client
    let title = `${clientName} ./.`

    // Check if opponent is included in mapped entity types and exists
    const includeOpponent =
      opponentEntity && (mappedTypes.includes('opponent') || props.isOpponentExported)

    if (includeOpponent) {
      const opponentName =
        opponentEntity.entity?.entity_type === EntityTypeEnum.LEGAL_PERSON
          ? `${opponentEntity.entity?.company_name || ''} ${opponentEntity.entity?.company_legal_form || 'GmbH'}`
          : opponentEntity.entity?.last_name || ''

      title = `${clientName} ./. ${opponentName}`
    }

    return title
  })

  const schema = {
    typeId: yup.string().required(t('typeIdRequired')),
    name: yup.string().required(t('nameRequired')),
    creationDate: yup.date().required(t('creationDateRequired'))
  }

  const {
    formData,
    formErrors,
    touchedFields,
    validate,
    handleFieldTouch,
    handleFieldChange,
    showErrors
  } = useFormWithValidation(
    {
      typeId: initialTypeId.value,
      creationDate: new Date().toISOString().split('T')[0],
      name: initialName.value,
      description: ''
    },
    schema
  )

  const handleSave = async (updatedData: GenericObject) => {
    emit('update:payload', updatedData)
  }

  // Watch initialName and update formData.name when it changes
  watch(
    initialName,
    (newValue) => {
      if (formData) {
        formData.name = newValue
      }
    },
    { immediate: false }
  )

  // Watch initialTypeId and update formData.typeId when it changes
  watch(
    initialTypeId,
    (newValue) => {
      if (formData) {
        formData.typeId = newValue
      }
    },
    { immediate: false }
  )

  // Watch formData for changes to emit updates
  watch(
    formData,
    (newValue) => {
      handleSave(newValue)
    },
    { deep: true }
  )

  onMounted(() => {
    // Check token validity when component mounts
    checkKleosTokenValidity()

    handleSave({
      typeId: initialTypeId.value,
      creationDate: '',
      name: initialName.value,
      description: ''
    })

    // Fetch user details, used to know the role of the current user
    if (!userStore.user) {
      fetchUser()
    }
  })

  defineExpose({
    validate,
    showErrors
  })
</script>

<template>
  <div class="rounded-xl border bg-white p-6 shadow-md">
    <h2 class="mb-4 text-2xl font-bold text-gray-900">{{ t('kleosSection') }}</h2>
    <AutoSavingForm
      :debounce-delay="debounceDelay"
      :throttle-interval="throttleInterval"
      class="space-y-6"
      @save="handleSave"
    >
      <div class="space-y-6">
        <div>
          <SelectField
            v-model="formData.typeId"
            :options="kleosCaseTypeOptions"
            name="typeId"
            :label="t('typeId')"
            :is-filterable="true"
            class="w-full"
            :error="formErrors?.value?.typeId ?? null"
            @update:model-value="(value: any) => handleFieldChange('typeId', value)"
            @focus="() => handleFieldTouch('typeId')"
          />
        </div>
        <div>
          <InputField
            v-model="formData.name"
            type="text"
            :label="t('name')"
            name="name"
            class="w-full"
            :error="formErrors?.value?.name ?? null"
            @update:model-value="(value: any) => handleFieldChange('name', value)"
            @focus="() => handleFieldTouch('name')"
          />
        </div>
        <div>
          <InputField
            v-model="formData.creationDate"
            type="date"
            :label="t('creationDate')"
            name="creationDate"
            class="w-full"
            :error="formErrors?.value?.creationDate ?? null"
            @update:model-value="(value: any) => handleFieldChange('creationDate', value)"
            @focus="() => handleFieldTouch('creationDate')"
          />
        </div>
        <TextAreaField
          v-model="formData.description"
          :label="t('description')"
          name="description"
          :error="formErrors?.value?.description ?? null"
          @update:model-value="(value: any) => handleFieldChange('description', value)"
          @focus="() => handleFieldTouch('description')"
        />
      </div>
    </AutoSavingForm>

    <!-- Authentication Expired Modal -->
    <div
      v-if="showAuthModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
    >
      <div class="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
        <h3
          v-if="isKleosExpiredToken"
          class="mb-4 text-xl font-bold"
        >
          {{ t('authExpiredTitle') }}
        </h3>
        <h3
          v-else
          class="mb-4 text-xl font-bold"
        >
          {{ t('missingAuthTitle') }}
        </h3>
        <p
          v-if="isKleosExpiredToken"
          class="mb-6"
        >
          {{ t('authExpiredMessage') }}
          <template v-if="userRole === RoleEnum.ADMIN">
            {{ t('adminShouldReauthMessage') }}
          </template>
          <template v-else>
            {{ t('userShouldContactAdminMessage') }}
          </template>
        </p>
        <p
          v-else
          class="mb-6"
        >
          {{ t('missingAuthMessage') }}
          <template v-if="userRole === RoleEnum.ADMIN">
            {{ t('adminShouldAuthMessage') }}
          </template>
          <template v-else>
            {{ t('userShouldContactAdminMessage') }}
          </template>
        </p>
        <div class="flex justify-end space-x-3">
          <button
            class="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700
              hover:bg-gray-50"
            @click="closeAuthModal"
          >
            <template v-if="userRole === RoleEnum.ADMIN">
              {{ t('cancel') }}
            </template>
            <template v-else>OK</template>
          </button>
          <button
            v-if="userRole === RoleEnum.ADMIN"
            class="rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700"
            @click="handleReAuthenticate"
          >
            {{ t('reAuthenticate') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
